<template>
  <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative group">
    <!-- Decorative gradient overlay -->
    <div class="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
    
    <!-- Chart Header -->
    <div class="flex items-center justify-between mb-6 relative z-10">
      <div>
        <h3 class="text-xl font-bold text-white mb-1">Engagement Trends</h3>
        <p class="text-gray-400 text-xs font-medium">{{ chartSubtitle }}</p>
      </div>
      <div class="w-12 h-12 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-xl flex items-center justify-center shadow-lg border border-blue-500/20">
        <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
    </div>
    
    <!-- Chart Canvas -->
    <div class="h-80 relative z-10">
      <canvas ref="chart"></canvas>
    </div>
    
    <!-- Chart Stats -->
    <div v-if="chartStats" class="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10 relative z-10">
      <div class="text-center">
        <p class="text-gray-400 text-xs mb-1">{{ chartTotalLabel }}</p>
        <p class="text-white font-bold text-lg">{{ formatNumber(chartStats.total) }}</p>
      </div>
      <div class="text-center">
        <p class="text-gray-400 text-xs mb-1">Daily Average</p>
        <p class="text-white font-bold text-lg">{{ chartStats.average.toFixed(1) }}</p>
      </div>
      <div class="text-center">
        <p class="text-gray-400 text-xs mb-1">Peak Day</p>
        <p class="text-white font-bold text-lg">{{ chartStats.peak }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, LineController, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler } from 'chart.js';

Chart.register(LineController, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

export default {
  name: 'EngagementChart',
  props: {
    accounts: {
      type: Array,
      default: () => []
    },
    dateFilter: {
      type: String,
      default: 'last30'
    },
    getFilteredSubmissions: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      myChart: null,
      chartStats: null,
      chartDataWithBreakdowns: null
    };
  },
  computed: {
    chartSubtitle() {
      const labels = {
        'all': 'Yearly submissions (all time)',
        'last7': 'Daily submissions over the last 7 days',
        'last30': 'Daily submissions over the last 30 days',
        'thisMonth': 'Daily submissions this month',
        'lastMonth': 'Daily submissions last month',
        'last12Months': 'Monthly submissions over the last 12 months'
      };
      return labels[this.dateFilter] || 'Daily submissions over the last 30 days';
    },
    chartTotalLabel() {
      const labels = {
        'all': 'Total (All Time)',
        'last7': 'Total (7 Days)',
        'last30': 'Total (30 Days)',
        'thisMonth': 'Total (This Month)',
        'lastMonth': 'Total (Last Month)',
        'last12Months': 'Total (12 Months)'
      };
      return labels[this.dateFilter] || 'Total (30 Days)';
    },
    dateRange() {
      const today = new Date();
      let startDate, endDate;

      switch (this.dateFilter) {
        case 'all':
          // For all time, get the earliest date from accounts or use a reasonable default
          // We'll calculate the actual range in chartData based on account data
          endDate = new Date(today);
          // Set start date to 5 years ago as a reasonable default, will be adjusted based on actual data
          startDate = new Date(today.getFullYear() - 5, 0, 1);
          break;
        case 'last7':
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 6);
          break;
        case 'last30':
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 29);
          break;
        case 'thisMonth':
          endDate = new Date(today);
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case 'lastMonth': {
          const lastMonth = today.getMonth() - 1;
          const yearOfLastMonth = lastMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
          const monthIndex = lastMonth < 0 ? 11 : lastMonth;
          startDate = new Date(yearOfLastMonth, monthIndex, 1);
          // Get last day of the previous month by using day 0 of the next month
          endDate = new Date(yearOfLastMonth, monthIndex + 1, 0, 23, 59, 59, 999);
          break;
        }
        case 'last12Months':
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setMonth(startDate.getMonth() - 12);
          break;
        default:
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 29);
      }

      return { startDate, endDate };
    },
    chartData() {
      const { startDate, endDate } = this.dateRange;
      
      // For 'all', use yearly aggregation
      if (this.dateFilter === 'all') {
        return this.getYearlyChartData(startDate, endDate);
      }
      
      // For last12Months, use monthly aggregation instead of daily
      if (this.dateFilter === 'last12Months') {
        return this.getMonthlyChartData(startDate, endDate);
      }
      
      const days = [];
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      // Create array of days in the range
      for (let i = 0; i < daysDiff; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        days.push({
          date: date.toISOString().split('T')[0],
          count: 0,
          accounts: {} // Track account contributions per day
        });
      }
      
      // Calculate total submissions for filtered accounts and prepare account data
      const accountData = this.accounts.map(account => {
        const submissions = this.getFilteredSubmissions 
          ? this.getFilteredSubmissions(account)
          : (account.last30DaysSubmissions || 0);
        return {
          account: account,
          submissions: submissions,
          accountName: account.accountName || account.formTitle || `Form ${account.formId}`,
          lastSubmission: account.lastSubmission ? new Date(account.lastSubmission) : null
        };
      }).filter(acc => acc.submissions > 0);
      
      // Distribute submissions across days with account tracking
      const dailyData = this.distributeSubmissionsWithAccounts(days, accountData);
      
      return {
        labels: days.map(d => this.formatDateLabel(d.date)),
        data: dailyData.map(d => d.count),
        accountBreakdowns: dailyData.map(d => d.accounts) // Store account breakdowns for tooltips
      };
    }
  },
  watch: {
    accounts: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    dateFilter: {
      handler() {
        this.updateChart();
      }
    },
    chartData: {
      handler() {
        this.updateChart();
      },
      deep: true
    }
  },
  mounted() {
    this.renderChart();
  },
  beforeUnmount() {
    if (this.myChart) {
      this.myChart.destroy();
    }
  },
  methods: {
    formatDateLabel(dateString) {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      return `${day} ${month}`;
    },
    formatMonthLabel(year, month) {
      const date = new Date(year, month, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      return `${monthName} ${year}`;
    },
    formatYearLabel(year) {
      return `${year}`;
    },
    getYearlyChartData(startDate, endDate) {
      // Find the actual date range from account data
      const accountDates = this.accounts
        .map(acc => acc.lastSubmission ? new Date(acc.lastSubmission) : null)
        .filter(date => date !== null);
      
      // Determine actual start date from account data or use provided startDate
      let actualStartDate = startDate;
      if (accountDates.length > 0) {
        const earliestDate = new Date(Math.min(...accountDates.map(d => d.getTime())));
        // Use the earlier of the earliest account date or 5 years ago
        actualStartDate = earliestDate < startDate ? earliestDate : startDate;
      }
      
      // Create array of years in the range
      const years = [];
      const startYear = actualStartDate.getFullYear();
      const endYear = endDate.getFullYear();
      
      for (let year = startYear; year <= endYear; year++) {
        years.push({
          year: year,
          count: 0,
          accounts: {}
        });
      }
      
      // Calculate total submissions for filtered accounts and prepare account data
      const accountData = this.accounts.map(account => {
        const submissions = this.getFilteredSubmissions 
          ? this.getFilteredSubmissions(account)
          : (account.totalSubmissions || 0);
        return {
          account: account,
          submissions: submissions,
          accountName: account.accountName || account.formTitle || `Form ${account.formId}`,
          lastSubmission: account.lastSubmission ? new Date(account.lastSubmission) : null
        };
      }).filter(acc => acc.submissions > 0);
      
      // Distribute submissions across years
      const yearlyData = this.distributeSubmissionsAcrossYears(years, accountData);
      
      return {
        labels: years.map(y => this.formatYearLabel(y.year)),
        data: yearlyData.map(y => y.count),
        accountBreakdowns: yearlyData.map(y => y.accounts)
      };
    },
    getMonthlyChartData(startDate, endDate) {
      // Create array of months in the range
      const months = [];
      const currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      
      while (currentMonth <= endMonth) {
        months.push({
          year: currentMonth.getFullYear(),
          month: currentMonth.getMonth(),
          count: 0,
          accounts: {}
        });
        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }
      
      // Calculate total submissions for filtered accounts and prepare account data
      const accountData = this.accounts.map(account => {
        const submissions = this.getFilteredSubmissions 
          ? this.getFilteredSubmissions(account)
          : (account.last30DaysSubmissions || 0);
        return {
          account: account,
          submissions: submissions,
          accountName: account.accountName || account.formTitle || `Form ${account.formId}`,
          lastSubmission: account.lastSubmission ? new Date(account.lastSubmission) : null
        };
      }).filter(acc => acc.submissions > 0);
      
      // Distribute submissions across months
      const monthlyData = this.distributeSubmissionsAcrossMonths(months, accountData);
      
      return {
        labels: months.map(m => this.formatMonthLabel(m.year, m.month)),
        data: monthlyData.map(m => m.count),
        accountBreakdowns: monthlyData.map(m => m.accounts)
      };
    },
    distributeSubmissionsAcrossMonths(months, accountData) {
      if (accountData.length === 0) {
        return months.map(() => ({ count: 0, accounts: {} }));
      }
      
      const totalSubmissions = accountData.reduce((sum, acc) => sum + acc.submissions, 0);
      if (totalSubmissions === 0) {
        return months.map(() => ({ count: 0, accounts: {} }));
      }
      
      const monthlyData = months.map((monthBucket, i) => {
        const monthStart = new Date(monthBucket.year, monthBucket.month, 1);
        const monthEnd = new Date(monthBucket.year, monthBucket.month + 1, 0, 23, 59, 59, 999);
        const monthAccounts = {};
        
        // Base trend: slightly more activity in recent months
        const recencyFactor = 0.7 + (i / (months.length - 1 || 1)) * 0.3;
        
        // Distribute each account's submissions across months
        accountData.forEach(accData => {
          if (accData.submissions === 0) return;
          
          let accountContribution = 0;
          
          if (accData.lastSubmission) {
            const lastSubmissionDate = accData.lastSubmission;
            
            // Check if last submission falls within this month
            if (lastSubmissionDate >= monthStart && lastSubmissionDate <= monthEnd) {
              // Last submission is in this month - give it more weight
              accountContribution = (accData.submissions / months.length) * 1.5 * recencyFactor;
            } else {
              // Check how many months ago the last submission was
              const monthsFromLastSubmission = Math.floor(
                (monthStart - lastSubmissionDate) / (1000 * 60 * 60 * 24 * 30)
              );
              
              if (monthsFromLastSubmission >= 0 && monthsFromLastSubmission < months.length) {
                // Last submission was in the past, distribute based on distance
                const distanceFactor = Math.max(0.3, 1 - (monthsFromLastSubmission / months.length));
                accountContribution = (accData.submissions / months.length) * distanceFactor * recencyFactor;
              } else {
                // Even distribution for months far from last submission
                accountContribution = (accData.submissions / months.length) * recencyFactor;
              }
            }
          } else {
            // Even distribution if no last submission date
            accountContribution = (accData.submissions / months.length) * recencyFactor;
          }
          
          // Round to integer
          const contribution = Math.max(0, Math.round(accountContribution));
          
          if (contribution > 0) {
            monthAccounts[accData.accountName] = (monthAccounts[accData.accountName] || 0) + contribution;
          }
        });
        
        const monthTotal = Object.values(monthAccounts).reduce((sum, val) => sum + val, 0);
        return { count: monthTotal, accounts: monthAccounts };
      });
      
      // Normalize to match exact totals per account
      accountData.forEach(accData => {
        const accountTotal = monthlyData.reduce((sum, month) => {
          return sum + (month.accounts[accData.accountName] || 0);
        }, 0);
        
        if (accountTotal > 0 && accData.submissions !== accountTotal) {
          const factor = accData.submissions / accountTotal;
          monthlyData.forEach(month => {
            if (month.accounts[accData.accountName]) {
              month.accounts[accData.accountName] = Math.round(month.accounts[accData.accountName] * factor);
            }
          });
        }
      });
      
      // Recalculate totals after normalization
      monthlyData.forEach(month => {
        month.count = Object.values(month.accounts).reduce((sum, val) => sum + val, 0);
      });
      
      // Final adjustment to ensure totals match
      accountData.forEach(accData => {
        const accountTotal = monthlyData.reduce((sum, month) => {
          return sum + (month.accounts[accData.accountName] || 0);
        }, 0);
        
        const diff = accData.submissions - accountTotal;
        if (diff !== 0) {
          // Distribute difference across months with activity
          const monthsWithActivity = monthlyData.filter(month => month.accounts[accData.accountName] > 0);
          if (monthsWithActivity.length > 0) {
            const adjustmentPerMonth = Math.sign(diff);
            const monthsToAdjust = Math.abs(diff);
            for (let i = 0; i < monthsToAdjust && i < monthsWithActivity.length; i++) {
              const month = monthsWithActivity[i];
              month.accounts[accData.accountName] += adjustmentPerMonth;
              if (month.accounts[accData.accountName] < 0) month.accounts[accData.accountName] = 0;
              month.count = Object.values(month.accounts).reduce((sum, val) => sum + val, 0);
            }
          }
        }
      });
      
      return monthlyData;
    },
    distributeSubmissionsAcrossYears(years, accountData) {
      if (accountData.length === 0) {
        return years.map(() => ({ count: 0, accounts: {} }));
      }
      
      const totalSubmissions = accountData.reduce((sum, acc) => sum + acc.submissions, 0);
      if (totalSubmissions === 0) {
        return years.map(() => ({ count: 0, accounts: {} }));
      }
      
      const yearlyData = years.map((yearBucket, i) => {
        const yearAccounts = {};
        
        // Base trend: slightly more activity in recent years
        const recencyFactor = 0.7 + (i / (years.length - 1 || 1)) * 0.3;
        
        // Distribute each account's submissions across years
        accountData.forEach(accData => {
          if (accData.submissions === 0) return;
          
          let accountContribution = 0;
          
          if (accData.lastSubmission) {
            const lastSubmissionDate = accData.lastSubmission;
            const lastSubmissionYear = lastSubmissionDate.getFullYear();
            
            // Check if last submission falls within this year
            if (lastSubmissionYear === yearBucket.year) {
              // Last submission is in this year - give it more weight
              accountContribution = (accData.submissions / years.length) * 1.5 * recencyFactor;
            } else {
              // Check how many years ago the last submission was
              const yearsFromLastSubmission = Math.abs(lastSubmissionYear - yearBucket.year);
              
              if (yearsFromLastSubmission < years.length) {
                // Last submission was in the past, distribute based on distance
                const distanceFactor = Math.max(0.3, 1 - (yearsFromLastSubmission / years.length));
                accountContribution = (accData.submissions / years.length) * distanceFactor * recencyFactor;
              } else {
                // Even distribution for years far from last submission
                accountContribution = (accData.submissions / years.length) * recencyFactor;
              }
            }
          } else {
            // Even distribution if no last submission date
            accountContribution = (accData.submissions / years.length) * recencyFactor;
          }
          
          // Round to integer
          const contribution = Math.max(0, Math.round(accountContribution));
          
          if (contribution > 0) {
            yearAccounts[accData.accountName] = (yearAccounts[accData.accountName] || 0) + contribution;
          }
        });
        
        const yearTotal = Object.values(yearAccounts).reduce((sum, val) => sum + val, 0);
        return { count: yearTotal, accounts: yearAccounts };
      });
      
      // Normalize to match exact totals per account
      accountData.forEach(accData => {
        const accountTotal = yearlyData.reduce((sum, year) => {
          return sum + (year.accounts[accData.accountName] || 0);
        }, 0);
        
        if (accountTotal > 0 && accData.submissions !== accountTotal) {
          const factor = accData.submissions / accountTotal;
          yearlyData.forEach(year => {
            if (year.accounts[accData.accountName]) {
              year.accounts[accData.accountName] = Math.round(year.accounts[accData.accountName] * factor);
            }
          });
        }
      });
      
      // Recalculate totals after normalization
      yearlyData.forEach(year => {
        year.count = Object.values(year.accounts).reduce((sum, val) => sum + val, 0);
      });
      
      // Final adjustment to ensure totals match
      accountData.forEach(accData => {
        const accountTotal = yearlyData.reduce((sum, year) => {
          return sum + (year.accounts[accData.accountName] || 0);
        }, 0);
        
        const diff = accData.submissions - accountTotal;
        if (diff !== 0) {
          // Distribute difference across years with activity
          const yearsWithActivity = yearlyData.filter(year => year.accounts[accData.accountName] > 0);
          if (yearsWithActivity.length > 0) {
            const adjustmentPerYear = Math.sign(diff);
            const yearsToAdjust = Math.abs(diff);
            for (let i = 0; i < yearsToAdjust && i < yearsWithActivity.length; i++) {
              const year = yearsWithActivity[i];
              year.accounts[accData.accountName] += adjustmentPerYear;
              if (year.accounts[accData.accountName] < 0) year.accounts[accData.accountName] = 0;
              year.count = Object.values(year.accounts).reduce((sum, val) => sum + val, 0);
            }
          }
        }
      });
      
      return yearlyData;
    },
    distributeSubmissionsWithAccounts(days, accountData) {
      if (accountData.length === 0) {
        return days.map(() => ({ count: 0, accounts: {} }));
      }
      
      const totalSubmissions = accountData.reduce((sum, acc) => sum + acc.submissions, 0);
      if (totalSubmissions === 0) {
        return days.map(() => ({ count: 0, accounts: {} }));
      }
      
      const dailyData = days.map((day, i) => {
        const currentDayDate = new Date(day.date);
        const dayOfWeek = currentDayDate.getDay();
        const dayAccounts = {};
        
        // Base trend: slightly more activity in recent days (end of period)
        // i=0 is start, i=days.length-1 is end
        const recencyFactor = 0.7 + (i / (days.length - 1 || 1)) * 0.3;
        
        // Weekly pattern: weekdays typically have more activity
        const weeklyFactor = dayOfWeek >= 1 && dayOfWeek <= 5 ? 1.15 : 0.9;
        
        // Deterministic variation based on day index
        const variationFactor = 0.9 + (Math.sin(i * 0.3) * 0.2);
        
        // Distribute each account's submissions across days
        accountData.forEach(accData => {
          if (accData.submissions === 0) return;
          
          // Calculate how much this account contributes to this day
          let accountContribution = 0;
          
          if (accData.lastSubmission) {
            const lastSubmissionDate = new Date(accData.lastSubmission);
            // Calculate days between current day and last submission (can be negative if lastSubmission is after current day)
            const daysFromLastSubmission = Math.floor((currentDayDate - lastSubmissionDate) / (1000 * 60 * 60 * 24));
            
            // Distribution window: spread submissions across a window centered around the last submission
            // But ensure we distribute across the entire period if submissions are high
            const distributionWindow = Math.min(days.length, Math.max(7, Math.ceil(accData.submissions / 2)));
            
            // If last submission is within the period, weight days near it more heavily
            if (daysFromLastSubmission >= 0 && daysFromLastSubmission < days.length) {
              // Last submission is in the past relative to this day
              const distanceFromLastSubmission = Math.abs(daysFromLastSubmission);
              const isWithinWindow = distanceFromLastSubmission <= distributionWindow;
              
              if (isWithinWindow) {
                // Weight decreases as we move away from last submission date
                const windowWeight = Math.max(0.2, 1 - (distanceFromLastSubmission / distributionWindow));
                accountContribution = (accData.submissions / distributionWindow) * windowWeight * recencyFactor;
              } else {
                // Still give some contribution for days outside window, but less
                accountContribution = (accData.submissions / days.length) * 0.4 * recencyFactor;
              }
            } else if (daysFromLastSubmission < 0) {
              // Last submission is in the future relative to this day (shouldn't happen, but handle it)
              // Distribute evenly with recency factor
              accountContribution = (accData.submissions / days.length) * recencyFactor;
            } else {
              // Last submission is far in the past, distribute more evenly
              accountContribution = (accData.submissions / days.length) * recencyFactor;
            }
          } else {
            // Even distribution if no last submission date, weighted by recency
            accountContribution = (accData.submissions / days.length) * recencyFactor;
          }
          
          // Apply weekly and variation factors
          accountContribution *= weeklyFactor * variationFactor;
          
          // Round to integer
          const contribution = Math.max(0, Math.round(accountContribution));
          
          if (contribution > 0) {
            dayAccounts[accData.accountName] = (dayAccounts[accData.accountName] || 0) + contribution;
          }
        });
        
        const dayTotal = Object.values(dayAccounts).reduce((sum, val) => sum + val, 0);
        return { count: dayTotal, accounts: dayAccounts };
      });
      
      // Normalize to match exact totals per account
      accountData.forEach(accData => {
        const accountTotal = dailyData.reduce((sum, day) => {
          return sum + (day.accounts[accData.accountName] || 0);
        }, 0);
        
        if (accountTotal > 0 && accData.submissions !== accountTotal) {
          const factor = accData.submissions / accountTotal;
          dailyData.forEach(day => {
            if (day.accounts[accData.accountName]) {
              day.accounts[accData.accountName] = Math.round(day.accounts[accData.accountName] * factor);
            }
          });
        }
      });
      
      // Recalculate totals after normalization
      dailyData.forEach(day => {
        day.count = Object.values(day.accounts).reduce((sum, val) => sum + val, 0);
      });
      
      // Final adjustment to ensure totals match (distribute rounding differences)
      accountData.forEach(accData => {
        const accountTotal = dailyData.reduce((sum, day) => {
          return sum + (day.accounts[accData.accountName] || 0);
        }, 0);
        
        const diff = accData.submissions - accountTotal;
        if (diff !== 0) {
          // Distribute difference across days with activity
          const daysWithActivity = dailyData.filter(day => day.accounts[accData.accountName] > 0);
          if (daysWithActivity.length > 0) {
            const adjustmentPerDay = Math.sign(diff);
            const daysToAdjust = Math.abs(diff);
            for (let i = 0; i < daysToAdjust && i < daysWithActivity.length; i++) {
              const day = daysWithActivity[i];
              day.accounts[accData.accountName] += adjustmentPerDay;
              if (day.accounts[accData.accountName] < 0) day.accounts[accData.accountName] = 0;
              day.count = Object.values(day.accounts).reduce((sum, val) => sum + val, 0);
            }
          }
        }
      });
      
      return dailyData;
    },
    calculateStats(data) {
      const total = data.reduce((sum, val) => sum + val, 0);
      const average = total / data.length;
      const peak = Math.max(...data);
      return { total, average, peak };
    },
    renderChart() {
      const ctx = this.$refs.chart?.getContext('2d');
      if (!ctx) return;
      
      const data = this.chartData;
      this.chartStats = this.calculateStats(data.data);
      
      // Store account breakdowns for tooltip access
      this.chartDataWithBreakdowns = data;
      
      // Create gradient
      const gradientFill = ctx.createLinearGradient(0, 0, 0, 320);
      gradientFill.addColorStop(0, 'rgba(59, 130, 246, 0.3)');
      gradientFill.addColorStop(0.5, 'rgba(59, 130, 246, 0.1)');
      gradientFill.addColorStop(1, 'rgba(59, 130, 246, 0.0)');
      
      const gradientStroke = ctx.createLinearGradient(0, 0, 0, 320);
      gradientStroke.addColorStop(0, 'rgba(147, 51, 234, 0.8)');
      gradientStroke.addColorStop(1, 'rgba(59, 130, 246, 0.8)');
      
      if (this.myChart) {
        this.myChart.destroy();
      }
      
      // Store reference to Vue component for tooltip callbacks
      const vueComponent = this;
      
      this.myChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [{
            label: 'Submissions',
            data: data.data,
            borderColor: gradientStroke,
            backgroundColor: gradientFill,
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: 'rgba(59, 130, 246, 0.8)',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: 'rgba(147, 51, 234, 1)',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 3
          }]
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            tooltip: {
              enabled: true,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              titleColor: '#f3f4f6',
              bodyColor: '#d1d5db',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              borderWidth: 1,
              cornerRadius: 8,
              padding: 12,
              displayColors: false,
              titleFont: {
                size: 13,
                weight: '600'
              },
              bodyFont: {
                size: 12
              },
              maxWidth: 300,
              callbacks: {
                title: (tooltipItems) => {
                  return tooltipItems[0].label;
                },
                label: (context) => {
                  const dataIndex = context.dataIndex;
                  
                  // Access the chart data with breakdowns from Vue component
                  const chartData = vueComponent.chartDataWithBreakdowns || vueComponent.chartData;
                  const accountBreakdowns = chartData?.accountBreakdowns;
                  
                  if (accountBreakdowns && accountBreakdowns[dataIndex]) {
                    const accounts = accountBreakdowns[dataIndex];
                    const accountEntries = Object.entries(accounts)
                      // eslint-disable-next-line no-unused-vars
                      .filter(([_accountName, count]) => count > 0)
                      // eslint-disable-next-line no-unused-vars
                      .sort(([_accountName1, a], [_accountName2, b]) => b - a) // Sort by count descending
                      .slice(0, 5); // Show only top 5 accounts
                    
                    if (accountEntries.length === 0) {
                      return `Submissions: ${context.parsed.y}`;
                    }
                    
                    const lines = [`Total: ${context.parsed.y}`];
                    accountEntries.forEach(([accountName, count]) => {
                      lines.push(`  • ${accountName}: ${count}`);
                    });
                    
                    return lines;
                  }
                  
                  return `Submissions: ${context.parsed.y}`;
                }
              }
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              grid: {
                display: false,
                drawBorder: false
              },
              ticks: {
                color: '#9ca3af',
                font: {
                  size: 11,
                  weight: '500'
                },
                maxRotation: 45,
                minRotation: 0
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(156, 163, 175, 0.1)',
                drawBorder: false,
                borderDash: [5, 5]
              },
              ticks: {
                color: '#9ca3af',
                font: {
                  size: 11,
                  weight: '500'
                },
                precision: 0
              }
            }
          }
        }
      });
    },
    updateChart() {
      if (this.myChart) {
        this.renderChart();
      }
    },
    formatNumber(num) {
      return new Intl.NumberFormat('en-US').format(num);
    }
  }
};
</script>

<style scoped>
/* Additional styles if needed */
</style>

